import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { resolveProfileReturn } from '../../utils/profileReturnTo'
import { X, Check, Shield, Scan, Search, ChevronRight } from 'lucide-react'
import { useI18n } from '../../i18n'
import { useProfileStore, useCaseStore, useUserStore } from '../../stores'
import CardScanner from '../../components/CardScanner'
import { processCardFile, persistCardData } from '../../utils/processCardImage'
import { cardUploadFailureMessage } from '../../utils/uploadRefusal'
import useCardScanPair from '../../hooks/useCardScanPair'
import useResolvedCardUrls from '../../hooks/useResolvedCardUrls'
import CardThumb from '../../components/CardThumb' // s110 b77
import { cardInlineSrc } from '../../utils/cardRef'
import ProfileSubpageLayout from '../../components/ProfileSubpageLayout'
import DobPicker from '../../components/DobPicker'
import EligibilityBadge from '../../components/EligibilityBadge'
import { eligibilityAnswer, eligibilityKey, subscriberDetailsBlank, answeredCheck, limitedResultFor, storedResultCovers, verdictWithheld, noticeForCoverage } from '../../utils/eligibilityLimit'
import MedicareIdLookup from '../../components/MedicareIdLookup'
import SecondaryInsuranceCard from '../../components/SecondaryInsuranceCard'
import { sameSecondaryCoverage } from '../../utils/insuranceDiscovery'
import InsuranceCandidateList from '../../components/InsuranceCandidateList'
import { COVERAGE_TYPES } from '../../data/medicalLists'
import { insuranceRegistryAPI, casesAPI, profileAPI } from '../../services/api'
import { payerPick, scanPayerPick, candidatePayerPick, regionChoices, regionPick, badgeHiddenByRegionCard, regionSettled, PAYER_ID_KEYS, PAYER_KEYS, payerKeysForWire } from '../../utils/payerPick'
import { DISCOVERY_CAP, needsPolicyholder, policyholderComplete, discoveryOutcome, outcomeMessageKey } from '../../utils/discoverySearch'
import { INSURANCE_DISCOVERY_ENABLED } from '../../config/featureFlags'
import { AUTOSAVE_MS, insuranceFormProblem, insuranceSaveBody, insuranceSaveKey, createSerialQueue } from '../../utils/insuranceAutosave'
import { MY_INSURANCE_SCREEN, MY_INSURANCE_PATH } from '../../config/insuranceLevel1'
import PrimaryCareDoctor from '../../components/PrimaryCareDoctor' // s101 (D497)
import { cardPcpFromScan } from '../../utils/pcpChoice'
import { roleCopayCents } from '../../utils/copayFigures'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

export default function ProfileInsurance() {
  const { t, hasText } = useI18n()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { profile, updateProfile, syncSection } = useProfileStore()
  const userId = useUserStore((s) => s.user?.id)
  const currentCase = useCaseStore((s) => s.currentCase)
  const allCases = useCaseStore((s) => s.cases)
  
  const [form, setForm] = useState({
    selfPay: false, coverageType: '',
    provider: '', providerId: '',
    ipa: '', ipaId: '',
    subscriberRelationship: 'Self',
    subscriberFirstName: '', subscriberLastName: '', subscriberDOB: '',
    memberId: '', groupNumber: '', cardFront: null, cardBack: null,
    copayPCP: '', copaySpecialist: '',
    // s101 (D497): the patient's primary care doctor (confirmed or picked) and the one the card prints.
    pcp: null, pcpFromCard: null,
  })
  const [isProcessing, setIsProcessing] = useState(false)
  // PROD-237 (P17 step 8): card photos still uploading. Save waits for them, because a save
  //   during an upload now keeps the stored card instead of writing the unsaved one.
  const [cardUploads, setCardUploads] = useState(0)
  const [eligibilityChecking, setEligibilityChecking] = useState(false)
  // PROD-691 (P4-3): the server's daily-limit answer, shown under the badge (null = none)
  const [limitNotice, setLimitNotice] = useState(null)
  // Q-C5 (Alex 2026-10-07): the 'carrier|memberId' the stored last_eligibility was checked for
  //   (null: not known). Null on load (PROD-822), then set by every real answer. A limit
  //   answer for another member ID shows no verdict (utils/eligibilityLimit.js limitedResultFor).
  const resultKeyRef = useRef(null)
  // PROD-713 (Profile half): the last real answer IN THIS MOUNT and what it was ASKED about
  //   (answeredCheck; null: none yet). Save pays no check when the card still shows that answer and
  //   it would ask the same (storedResultCovers). Review A1: never set from the loaded record, whose
  //   result may belong to an older member ID (autosave, unmount save, md staff edits).
  const checkedInputsRef = useRef(null)
  // ★ PROD-821 review REVIEW_PNEXT2 B-2: counts "Use this plan" picks. A check asked before a pick and
  //   answered after it was asked about the OLD payer, so its answer never sets checkedInputsRef
  //   (the onboarding twin is the run's `stale` mark).
  const pickSeqRef = useRef(0)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [scannerOpen, setScannerOpen] = useState(null)
  const [carrierQuery, setCarrierQuery] = useState('')
  const [ipaQuery, setIpaQuery] = useState('')
  const [showCarrierList, setShowCarrierList] = useState(false)
  const [dynamicCarriers, setDynamicCarriers] = useState(null)
  const [dynamicIPAs, setDynamicIPAs] = useState([])
  const [stediResults, setStediResults] = useState([])
  const [stediLoading, setStediLoading] = useState(false)
  // v242.64.0 (TF-11): "not in the directory" and "we could not reach the
  // directory" must not render as the same sentence.
  const [stediError, setStediError] = useState(null)
  const loadedRef = useRef(false)
  const memberIdInputRef = useRef(null)  // v222.4.11 — for inline "Edit member ID" CTA from EligibilityBadge

  // ★ U2-INS C5 (TEST 3.09): the screen saves itself. utils/insuranceAutosave.js says why every
  //   insurance request of this screen runs through ONE serial queue: none of them may overlap
  //   another, or a server write-back can revert an edit (or an edit a server result).
  //   savedKeyRef: the identity of what the server holds from this form (the loaded record, then
  //   each successful save). The form is dirty when it differs.
  const queueRef = useRef(null)
  if (!queueRef.current) queueRef.current = createSerialQueue()
  const formRef = useRef(form)
  formRef.current = form
  const cardUploadsRef = useRef(0)
  cardUploadsRef.current = cardUploads
  const savedKeyRef = useRef(null)
  const savedFormRef = useRef(null)
  const autosaveTimerRef = useRef(null)
  const aliveRef = useRef(true)
  const [saveState, setSaveState] = useState('idle') // idle | saving | saved | failed
  // ★ U2-INS C2 (TEST 3.02): the primary's Search. searchStatus is a discoveryOutcome kind,
  //   'searching', or 'idle'; searchesLeft is the server's count (display only, it recounts).
  const [searchStatus, setSearchStatus] = useState('idle')
  const [searchesLeft, setSearchesLeft] = useState(() =>
    Number.isInteger(profile?.insurance?.discovery_result?.searchesLeft) ? profile.insurance.discovery_result.searchesLeft : null)
  const [searchCandidates, setSearchCandidates] = useState([])
  const storedSearchesLeft = profile?.insurance?.discovery_result?.searchesLeft
  useEffect(() => {
    if (searchesLeft === null && Number.isInteger(storedSearchesLeft)) setSearchesLeft(storedSearchesLeft)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedSearchesLeft])
  const isDirty = () => savedKeyRef.current !== null && insuranceSaveKey(formRef.current) !== savedKeyRef.current

  // v224.5.7: debounced live Stedi payer typeahead for the carrier picker.
  useEffect(() => {
    const q = carrierQuery.trim()
    if (q.length < 2) { setStediResults([]); setStediLoading(false); return }
    setStediLoading(true)
    const tid = setTimeout(async () => {
      const { payers, error } = await insuranceRegistryAPI.searchPayersDetailed(q)
      setStediResults(Array.isArray(payers) ? payers : [])
      setStediError(error || null)
      setStediLoading(false)
    }, 350)
    return () => clearTimeout(tid)
  }, [carrierQuery])

  useEffect(() => {
    if (loadedRef.current) return
    loadedRef.current = true
    const loadRegistry = async () => {
      const [carriers, ipas] = await Promise.all([
        insuranceRegistryAPI.getCarriers(),
        insuranceRegistryAPI.getIPAs(),
      ])
      if (carriers) setDynamicCarriers(carriers)
      if (ipas) setDynamicIPAs(ipas)
    }
    loadRegistry()
  }, [])

  useEffect(() => {
    const insurance = profile?.insurance
    const hasData = insurance && (insurance.selfPay || insurance.coverageType || insurance.provider || insurance.memberId || insurance.completed)
    if (hasData && !hasLoaded) {
      setForm({
        selfPay: insurance.selfPay || false,
        coverageType: insurance.coverageType || insurance.planType || '',
        provider: insurance.provider || '', providerId: insurance.providerId || '',
        ipa: insurance.ipa || '', ipaId: insurance.ipaId || '',
        subscriberRelationship: insurance.subscriberRelationship || 'Self',
        subscriberFirstName: insurance.subscriberFirstName || '',
        subscriberLastName: insurance.subscriberLastName || '',
        subscriberDOB: insurance.subscriberDOB || '',
        memberId: insurance.memberId || '', groupNumber: insurance.groupNumber || '',
        cardFront: insurance.cardFront || null, cardBack: insurance.cardBack || null,
        copayPCP: insurance.copayPCP || '', copaySpecialist: insurance.copaySpecialist || '',
        pcp: insurance.pcp || null, pcpFromCard: insurance.pcpFromCard || null,
        stediPayerName: insurance.stediPayerName || '',
        payerMatchConfidence: insurance.payerMatchConfidence || '',
        // L1 step 6: regionChoices() reads it; the save sends back what was stored.
        payerMatchSource: insurance.payerMatchSource || '',
        payerNeedsConfirmation: insurance.payerNeedsConfirmation || false,
        payerCandidates: insurance.payerCandidates || [],
      })
      // ★ PROD-822 (review r2 "decide 1", Q-C5): the loaded result's coverage is NOT known (it may be
      //   an older member ID's: autosave, unmount save, md staff edits), so a limit answer before a
      //   real answer in this mount shows no verdict, only when the ID can be checked. Measured at
      //   589f548a: resultKeyRef took the record's key, so (once PROD-589's limit is wired) member ID B
      //   on file with A's result, Save at the limit -> A's result shown as B's.
      resultKeyRef.current = null
      checkedInputsRef.current = null // PROD-713 review A1: a loaded result was not answered in this mount
      setHasLoaded(true)
    } else if (!hasData && !hasLoaded && profile?.profileLoaded && savedKeyRef.current === null) {
      // C5: the profile is loaded and holds no insurance yet: the empty form is the baseline.
      savedFormRef.current = formRef.current
      savedKeyRef.current = insuranceSaveKey(formRef.current)
    }
  }, [profile, hasLoaded])

  // C5: what the server holds is what was loaded; only an edit after this is saved. Declared
  //   before the copay effect below, so in the load's commit it reads the loaded form (formRef is
  //   set during render) and the copay fill then moves the baseline with it.
  useEffect(() => {
    if (!hasLoaded) return
    savedFormRef.current = formRef.current
    savedKeyRef.current = insuranceSaveKey(formRef.current)
  }, [hasLoaded])

  // v228.11.8: Copay from eligibility → copayment fields. When the 271 benefit
  // response carries plan copays, populate PCP / Specialist so the patient sees
  // their real copays without typing them. Cents → whole dollars. Only fills a
  // field that is still empty, so any manual entry always wins.
  useEffect(() => {
    if (!hasLoaded) return
    const elig = profile?.insurance?.last_eligibility
    if (!elig) return
    const toDollars = (c) => (c == null || isNaN(Number(c)) ? '' : String(Math.round(Number(c) / 100)))
    const pcpFromElig = toDollars(roleCopayCents(elig, 'pcp'))
    const specFromElig = toDollars(roleCopayCents(elig, 'specialist'))
    setForm(prev => {
      let changed = false
      const next = { ...prev }
      if (!String(prev.copayPCP || '').trim() && pcpFromElig !== '') { next.copayPCP = pcpFromElig; changed = true }
      if (!String(prev.copaySpecialist || '').trim() && specFromElig !== '') { next.copaySpecialist = specFromElig; changed = true }
      return changed ? next : prev
    })
    // C5: a copay filled from the eligibility answer is the server's data, not an edit: it moves
    //   the baseline the same way, so it alone never starts an autosave (no write on page load).
    if (savedFormRef.current) {
      const base = { ...savedFormRef.current }
      if (!String(base.copayPCP || '').trim() && pcpFromElig !== '') base.copayPCP = pcpFromElig
      if (!String(base.copaySpecialist || '').trim() && specFromElig !== '') base.copaySpecialist = specFromElig
      savedFormRef.current = base
      savedKeyRef.current = insuranceSaveKey(base)
    }
  }, [profile?.insurance?.last_eligibility, hasLoaded])

  // v222.4.11 — Deep-link from EligibilityBadge inline CTAs on other screens.
  // ?action=scan opens the front-of-card scanner; ?focus=memberId scrolls + focuses.
  useEffect(() => {
    if (!hasLoaded) return
    const action = searchParams.get('action')
    const focus = searchParams.get('focus')
    if (action === 'scan') {
      setScannerOpen('cardFront')
    } else if (focus === 'memberId') {
      // Defer until layout settles
      setTimeout(() => {
        const el = memberIdInputRef.current
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          el.focus()
        }
      }, 300)
    }
  }, [hasLoaded, searchParams])

  // ★★ PROD-431 (1), patient push 3a: the region card's "no payer id" guard reads the STORED
  //   ids too. The form never loads payerId / payer_id / stediPayerId / primaryPayerId (by
  //   design: a save without a re-pick sends no payer key, so the server keeps its id —
  //   check-payer-repick PR-5), so regionChoices(form) could not see a staff-set id: the card
  //   showed over a record that already routes, and the patient's pick replaced the staff's id.
  //   The form's own keys still win (a pick, or "Pick a different one", sets them).
  const storedPayerIds = Object.fromEntries(PAYER_ID_KEYS.map((k) => [k, profile?.insurance?.[k]]).filter(([, v]) => v !== undefined && v !== null))
  const regionRecord = { ...storedPayerIds, ...form }

  // C5: ONE save of the form as it is when the save RUNS (after whatever was queued before it).
  //   Not queued itself: callers run it inside queueRef.current.run, alone or before the request
  //   that must read it. Skipped while a card photo uploads (P17 step 8; the autosave effect
  //   re-arms when the upload ends) and when nothing changed (unless `force`).
  //   Returns { success, body, unchanged?, uploading? }.
  const doSave = async ({ complete = false, force = false } = {}) => {
    if (cardUploadsRef.current > 0) return { success: false, uploading: true }
    const snap = formRef.current
    const key = insuranceSaveKey(snap)
    if (!force && key === savedKeyRef.current) return { success: true, unchanged: true, body: insuranceSaveBody(snap) }
    // m1: completed follows the form: true when Save's checks pass, false when they do not.
    const body = insuranceSaveBody(snap, { complete: complete === true || insuranceFormProblem(snap, COVERAGE_TYPES) === null })
    if (aliveRef.current) setSaveState('saving')
    const result = await syncSection('insurance', body)
    if (result && result.success) {
      // ★ U2 push 2 review r1 M3: the server answers a save with the payer keys it resolved
      //   (ensure_stedi_payer_id: a typed carrier becomes payerId 60054, needsConfirmation). The
      //   form kept the ones it sent (a typed pick's payerId ''), so the NEXT autosave sent those
      //   back and the shallow merge wiped the resolved id. The stored keys are copied into the
      //   form and into the baseline, unless the patient picked another carrier while the save
      //   ran (that newer pick stays and is the next save). Only the keys the form already
      //   carries: a form that never picked holds no payer id, so it still sends none and the
      //   server keeps its own (check-payer-repick PR-5).
      const serverKeys = Object.fromEntries(Object.entries(result.storedInsurance ? payerKeysForWire(result.storedInsurance) : {}).filter(([k]) => k in snap))
      const savedSnap = { ...snap, ...serverKeys }
      savedFormRef.current = savedSnap
      savedKeyRef.current = insuranceSaveKey(savedSnap)
      if (Object.keys(serverKeys).length && PAYER_KEYS.every((k) => formRef.current?.[k] === snap[k])) {
        formRef.current = { ...formRef.current, ...serverKeys }
        if (aliveRef.current) setForm((prev) => ({ ...prev, ...serverKeys }))
      }
      if (aliveRef.current && insuranceSaveKey(formRef.current) === savedKeyRef.current) setSaveState('saved')
    } else if (aliveRef.current) setSaveState('failed')
    return { ...(result || { success: false }), body }
  }
  const runExclusive = (task) => queueRef.current.run(task)

  // C5: the autosave. AUTOSAVE_MS after the last edit, with no navigation, no eligibility call
  //   and no validation alert. It waits while a card photo uploads (the effect re-runs when
  //   cardUploads changes). A change of form or of cardUploads clears the pending timer.
  useEffect(() => {
    if (savedKeyRef.current === null) return // the stored record is not loaded yet
    if (cardUploadsRef.current > 0) return // a card photo is uploading; cardUploads is a dep, so this re-runs when it ends
    if (!isDirty()) return
    const tid = setTimeout(() => { runExclusive(() => doSave()).catch(() => {}) }, AUTOSAVE_MS)
    autosaveTimerRef.current = tid
    return () => clearTimeout(tid)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, cardUploads, hasLoaded])

  // C5: leaving the screen with an edit not yet saved saves it (the store action outlives the
  //   screen); aliveRef stops the status line from being set after the unmount.
  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
      clearTimeout(autosaveTimerRef.current)
      if (isDirty()) runExclusive(() => doSave()).catch(() => {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSave = async () => {
    if (cardUploads > 0) return // P17 step 8: a card photo is still uploading
    const problem = insuranceFormProblem(form, COVERAGE_TYPES)
    if (problem === 'coverage') { alert('Please select a coverage type'); return }
    if (problem === 'carrier') { alert('Please select an insurance carrier'); return }
    if (problem === 'member') { alert('Please enter your Member ID'); return }
    clearTimeout(autosaveTimerRef.current)
    setIsProcessing(true)
    // C5: queued behind any save or search already running, and it sends the form as it is then.
    const saved = await runExclusive(() => doSave({ complete: true, force: true }))
    const insuranceData = saved.body
    const result = saved
    setIsProcessing(false)
    // ★ U2-INS push 2 review r1 M2: the offline fallback MERGES the body onto the store's section,
    //   as the server's shallow merge would. It replaced the section with the form's keys, so
    //   discovery_result, secondary, last_eligibility_secondary (every key the body does not
    //   carry) were gone from the store: Home then ran a paid 90-day re-check nobody asked for,
    //   and the secondary card's Search built its PUT from no stored secondary and overwrote it.
    if (!result.success) {
      const currentIns = useProfileStore.getState().profile?.insurance || {}
      updateProfile({ insurance: { ...currentIns, ...insuranceData } })
    }

    // v227.13.0: Fire a practice-NPI eligibility check silently after save — this is
    // doctor-independent (plan-level benefits), so it populates the status card even
    // before a doctor is chosen. Self-pay skips. Soft-fails server-side.
    // ★ PROD-713 (Profile half): not when the result on the card was checked for exactly what this
    //   save would ask (same carrier, member ID and policyholder). Measured at 5244fc2e: every
    //   successful Save paid one check, also for a copay edit or an unchanged form.
    //   Review B2: for a Self policy the subscriber is the patient, so their name and DOB are inputs.
    const ident = useProfileStore.getState().profile?.identification
    const pickSeq = pickSeqRef.current // B-2: a pick after this makes the answer the old payer's
    const covered = result.success && storedResultCovers(useProfileStore.getState().profile?.insurance, resultKeyRef.current, checkedInputsRef.current, insuranceData, ident)
    if (result.success && !covered && !form.selfPay && form.coverageType && form.coverageType !== 'self-pay' && form.coverageType !== 'hsa' && userId && !subscriberDetailsBlank(insuranceData, ident)) { // PROD-1196: no 270 with a blank name or birth date
      // Fire and forget — never block navigation
      profileAPI.runPracticeEligibility(userId)
        .then((resp) => {
          // ★ M2: onto the store's section as it is NOW (the save already merged the body into it),
          //   never `{ ...insuranceData }`, which dropped every key the body does not carry.
          // PROD-696 (P4): through the one reader; a limit answer's null leaves the card as it was.
          // Q-C5: a limit answer for a changed member ID stores and shows no verdict.
          const shown = limitedResultFor(eligibilityAnswer(resp), eligibilityKey(insuranceData), resultKeyRef.current)
          resultKeyRef.current = shown.resultKey
          if (shown.eligibility && !shown.notice && pickSeqRef.current === pickSeq) checkedInputsRef.current = answeredCheck(insuranceData, shown.eligibility, ident) // PROD-713: a real result (B-2: no pick since)
          if (aliveRef.current) setLimitNotice(shown.notice)
          if (shown.eligibility && updateProfile) {
            const currentIns = useProfileStore.getState().profile?.insurance || {}
            updateProfile({ insurance: { ...currentIns, last_eligibility: shown.eligibility } })
          }
        })
        .catch((e) => { console.warn('[ELIGIBILITY] silent check failed:', e?.message || e) })
    }

    // ★ P7 P2 (TEST 2.09) — a DeliverScreen reopen sent us here mid-flow; go back to the
    //   SAME case route with its draft, instead of always landing on the Profile tab.
    const { path, state } = resolveProfileReturn(location.state)
    navigate(path, { state })
  }

  // v227.13.0: Manual re-check (EligibilityBadge "Re-check"/"Refresh"). Uses the
  // practice-NPI check so it works on the profile screen without a shared doctor.
  // ★ U2-INS C5 (TEST 3.09): the check reads the SAVED profile, so it checks what is on screen
  //   only after that is saved. In one queued task: the save first (a pending or running one is
  //   waited for), then the check. A failed save runs no check and says so. While a card photo
  //   uploads, nothing runs (Save shows the upload).
  const handleRecheckEligibility = async () => {
    if (!userId || cardUploadsRef.current > 0) return
    clearTimeout(autosaveTimerRef.current)
    setEligibilityChecking(true)
    const pickSeq = pickSeqRef.current // B-2: a pick after this makes the answer the old payer's
    try {
      const out = await runExclusive(async () => {
        const saved = await doSave()
        if (!saved.success) return { saved }
        const ident = useProfileStore.getState().profile?.identification // review B2: what the check reads
        return { saved, ident, resp: await profileAPI.runPracticeEligibility(userId) }
      })
      if (!out.saved.success) { if (aliveRef.current && !out.saved.uploading) setSaveState('failed'); return }
      // PROD-696 (P4): through the one reader; a limit answer's null leaves the card as it was.
      // Q-C5: a limit answer for a changed member ID (the one this save sent) stores and shows no
      //   verdict, only when this ID can be checked.
      const shown = limitedResultFor(eligibilityAnswer(out.resp), eligibilityKey(out.saved.body), resultKeyRef.current)
      resultKeyRef.current = shown.resultKey
      if (shown.eligibility && !shown.notice && pickSeqRef.current === pickSeq) checkedInputsRef.current = answeredCheck(out.saved.body, shown.eligibility, out.ident) // PROD-713: a real result (B-2: no pick since)
      if (aliveRef.current) setLimitNotice(shown.notice)
      if (shown.eligibility) {
        const currentIns = useProfileStore.getState().profile?.insurance || {}
        updateProfile({ insurance: { ...currentIns, last_eligibility: shown.eligibility } })
      }
    } catch (e) {
      console.warn('[ELIGIBILITY] recheck failed:', e?.message || e)
    } finally {
      if (aliveRef.current) setEligibilityChecking(false)
    }
  }

  // ★ U2-INS C2 (TEST 3.02): Search for the primary. The server searches with the SAVED profile
  //   (the policyholder's details when it is not "Me", U-1), so in ONE queued task the screen's
  //   save runs first (C5), then the search; a failed save sends no search. Unchanged details
  //   answer the stored result for free ('same_inputs', U-5). The result is the server's: it is
  //   kept in the store as the server wrote it, never PUT back.
  const handleSearch = async () => {
    if (!userId || searchStatus === 'searching' || cardUploadsRef.current > 0) return
    clearTimeout(autosaveTimerRef.current)
    setSearchStatus('searching')
    setSearchCandidates([])
    try {
      const out = await runExclusive(async () => {
        const saved = await doSave()
        if (!saved.success) return { saved }
        return { saved, resp: await profileAPI.runInsuranceDiscovery(userId, { target: 'primary', reason: 'search' }) }
      })
      if (!aliveRef.current) return
      if (!out.saved.success) { setSearchStatus('idle'); if (!out.saved.uploading) setSaveState('failed'); return }
      const o = discoveryOutcome(out.resp)
      if (o.searchesLeft !== null) setSearchesLeft(o.searchesLeft)
      if (o.discovery) {
        const cur = useProfileStore.getState().profile?.insurance || {}
        updateProfile({ insurance: { ...cur, discovery_result: o.discovery } })
      }
      if (o.kind === 'found') setSearchCandidates(o.discovery.candidates)
      setSearchStatus(o.kind)
    } catch (e) {
      if (aliveRef.current) setSearchStatus('failed')
    }
  }
  // "Use this plan": the candidate fills the form; the autosave saves it.
  // ★ U2 push 2 review r1 m4: the result is also marked applied (acknowledged, the index, the
  //   time), as Home's applyDiscoveryResult marks it. It stayed unacknowledged, so Home's toast
  //   then offered the plan just applied here ("We found a policy with …"), and its sheet would
  //   re-apply it over the whole section. The write carries discovery_result ALONE (the server's
  //   shallow merge leaves every other key) and runs in the screen's queue, after the search that
  //   wrote the result.
  // ★ PROD-482 (U2 push 2 review r2's notes on m4): two gaps of that mark.
  //   * The mark was written only over an UNACKNOWLEDGED result. A result already acknowledged
  //     (applied before from Home or onboarding, or dismissed; a same-details Search hands it
  //     back as stored) kept its old appliedCandidateIndex when another candidate was used here,
  //     and orella-api reads that index (rcm_service._discovery_primary_plan_keys) to drop the
  //     primary plan from a secondary search's free answer: the plan the patient left could no
  //     longer be offered as the secondary. Now the mark is written whenever the stored result
  //     does not already name this candidate, and only when the stored result still lists this
  //     candidate at this index (a result replaced since the search is not marked).
  //   * A failed PUT of the mark left the store saying acknowledged while the server did not: the
  //     next whole-section write from the store, or the next load, disagreed with this session.
  //     Now a failed mark is undone in the store, if the store still holds it.
  const applySearchCandidate = async (idx) => {
    const cand = searchCandidates[idx]
    if (!cand) return { success: false }
    setForm(prev => ({ ...prev, ...candidatePayerPick(cand), memberId: cand.memberIdGuess || prev.memberId }))
    // ★ PROD-821 (review B-r2-1): the candidate can keep the form's eligibilityKey (the same
    //   normalized carrier name and member ID, no registry slug) while it routes the 270 to ANOTHER
    //   payerId. The answer this mount received went to the old payer, so the next Save asks again.
    //   Measured at 589f548a (review proof_r2 R1): storedResultCovers true, no check, the old payer's
    //   not_found kept on the card and on the server.
    checkedInputsRef.current = null
    pickSeqRef.current += 1 // review REVIEW_PNEXT2 B-2: a check still out answers for the old payer
    const dr = useProfileStore.getState().profile?.insurance?.discovery_result
    const listed = dr && Array.isArray(dr.candidates) ? dr.candidates[idx] : null
    const sameCandidate = !!listed && (listed.memberIdGuess || '') === (cand.memberIdGuess || '') && (listed.payerId || '') === (cand.payerId || '')
      && (listed.payerName || '') === (cand.payerName || '')
    if (sameCandidate && (dr.acknowledged !== true || dr.appliedCandidateIndex !== idx)) {
      const applied = { ...dr, acknowledged: true, appliedCandidateIndex: idx, appliedAt: new Date().toISOString() }
      const cur = useProfileStore.getState().profile?.insurance || {}
      updateProfile({ insurance: { ...cur, discovery_result: applied } })
      runExclusive(() => profileAPI.update({ insurance: { discovery_result: applied } }))
        .catch((e) => {
          console.warn('[DISCOVERY] marking the result applied failed:', e?.status || e?.message || e)
          const now = useProfileStore.getState().profile?.insurance || {}
          if (now.discovery_result === applied) updateProfile({ insurance: { ...now, discovery_result: dr } })
        })
    }
    return { success: true }
  }
  const searchBlocked = searchStatus === 'searching' || searchesLeft === 0
    || (needsPolicyholder(form.subscriberRelationship) && !policyholderComplete(form))

  // v228.8.6: defer insurance auto-fill + payer matching until BOTH sides are
  // captured. The back's card-printed payer ID wins over the front's brand
  // name (fixes "Health Net" → PAYER_NOT_MAPPED). Shared with onboarding via
  // useCardScanPair so the modal behaves identically everywhere.
  const cardPair = useCardScanPair('insurance', (extracted) => {
    let detectedCoverage = form.coverageType
    if (extracted.planType) {
      const ptLower = extracted.planType.toLowerCase()
      const match = COVERAGE_TYPES.find(c => ptLower.includes(c.id))
      if (match) detectedCoverage = match.id
    }
    let detectedIPA = form.ipa, detectedIPAId = form.ipaId
    if (extracted.ipa || extracted.medicalGroup) {
      const ipaName = extracted.ipa || extracted.medicalGroup
      const found = dynamicIPAs.find(i => ipaName.toLowerCase().includes(i.name.toLowerCase().split(' ')[0].toLowerCase()))
      if (found) { detectedIPA = found.name; detectedIPAId = found.id }
      else { detectedIPA = ipaName; detectedIPAId = 'custom' }
    }
    // ★ PROD-187 (P12): the scan's payer verdict replaces the WHOLE payer key set, through
    //   the owner, and only when the scan changes the carrier (utils/payerPick.js). The old
    //   per-key `extracted.x || prev.x` merge kept the previous payer's id under every key
    //   the scan did not carry, and the server then kept routing to it.
    setForm(prev => ({ ...prev,
      ...(scanPayerPick(extracted, prev) || {}),
      coverageType: detectedCoverage || prev.coverageType, ipa: detectedIPA || prev.ipa, ipaId: detectedIPAId || prev.ipaId, memberId: extracted.memberId || prev.memberId, groupNumber: extracted.groupNumber || prev.groupNumber,
      copayPCP: extracted.copayOfficeVisit ? extracted.copayOfficeVisit.replace(/[^0-9.]/g, '') : prev.copayPCP,
      copaySpecialist: extracted.copaySpecialist ? extracted.copaySpecialist.replace(/[^0-9.]/g, '') : prev.copaySpecialist,
      // s101 (D497): the PCP printed on the card, offered for confirmation (never saved as the PCP by itself).
      pcpFromCard: cardPcpFromScan(extracted) || prev.pcpFromCard,
    }))
  })

  const handleCardCapture = (side, cardData, scanToken) => {
    // PROD-1189b: a result whose side was cleared or replaced since its extract started is dropped.
    if (scanToken && !cardPair.isCurrent(scanToken)) return
    // Store the image immediately so the slot shows captured; DEFER the
    // auto-fill + payer match until both sides are in (the back carries the
    // payer ID the resolver prefers).
    const token = scanToken || cardPair.beginSide(side)
    setForm(prev => ({ ...prev, [side]: cardData }))
    cardPair.captureSide(side, cardData, token)
    setScannerOpen(null)
    // v242.37.0: upload the camera shot to storage so it gets a durable storagePath
    // (like onboarding). Without this, _slimCard drops the base64 on save and the
    // card can't be re-presigned on read → broken image. Merge when it lands.
    setCardUploads((n) => n + 1)
    persistCardData(cardData, 'Insurance', side === 'cardFront' ? 'front' : 'back').then((enriched) => {
      if (!cardPair.isCurrent(token)) return // PROD-1189b: removed or replaced meanwhile
      if (enriched && enriched.storagePath) {
        setForm(prev => ({ ...prev, [side]: enriched }))
        cardPair.captureSide(side, enriched, token)
      } else if (enriched && enriched.uploadError) {
        // ★ P2 Release 3: the photo was NOT stored and will not survive the save. Say so.
        alert(cardUploadFailureMessage(t, enriched.uploadError))
      }
    }).catch(() => {}).finally(() => setCardUploads((n) => Math.max(0, n - 1)))
  }

  const selectCoverage = (id) => {
    const isSP = id === 'self-pay', isHSA = id === 'hsa'
    setForm(prev => ({ ...prev, coverageType: id, selfPay: isSP, ...(isSP || isHSA ? { ...payerPick({ kind: 'clear' }), ipa: '', ipaId: '' } : {}) }))
    setShowCarrierList(false); setCarrierQuery(''); setIpaQuery('')
  }
  const selectCarrier = (item) => {
    // item is a Stedi payer {stediId, primaryPayerId, displayName, eligibility} or custom {id, name}
    // ★ PROD-187 (P12): the whole payer key set, through the owner. Profile routes
    //   stedi-first, as it always has; a typed name carries no routable id anywhere.
    const isStedi = !!(item.stediId || item.primaryPayerId)
    const name = item.displayName || item.name || ''
    setForm(prev => ({ ...prev,
      ...(isStedi
        ? payerPick({ kind: 'registry', stediId: item.stediId, primaryPayerId: item.primaryPayerId, name, order: 'stedi-first', source: 'patient_pick', eligibility: item.eligibility })
        : payerPick({ kind: 'typed', name, slug: item.id })),
    }))
    setCarrierQuery(''); setShowCarrierList(false); setStediResults([])
  }
  const selectIPA = (item) => { setForm(prev => ({ ...prev, ipa: typeof item === 'string' ? item : item.name, ipaId: typeof item === 'string' ? 'custom' : item.id })); setIpaQuery('') }
  const removeFile = (side) => { setForm(prev => ({ ...prev, [side]: null })); cardPair.clearSide(side) }
  const getImageUrl = (card) => cardInlineSrc(card)
  const cardUrls = useResolvedCardUrls({ cardFront: form.cardFront, cardBack: form.cardBack })
  const showSubscriberInfo = form.subscriberRelationship && form.subscriberRelationship !== 'Self'

  const selectedCoverage = COVERAGE_TYPES.find(c => c.id === form.coverageType)
  const needsCarrier = selectedCoverage?.needsCarrier
  const needsIPA = selectedCoverage?.needsIPA
  const needsMember = selectedCoverage && !['self-pay', 'hsa'].includes(form.coverageType)

  const carrierQ = carrierQuery.toLowerCase().trim()
  const ipaQ = ipaQuery.toLowerCase().trim()
  const filteredIPAs = ipaQ ? dynamicIPAs.filter(i => i.active !== false && i.name.toLowerCase().includes(ipaQ)) : []

  const canSave = form.coverageType === 'self-pay' || form.coverageType === 'hsa' || (form.coverageType && form.provider && form.memberId)
  const inputCls = "w-full px-3.5 py-2.5 border-[1.5px] border-gray-200 rounded-[14px] text-[13px] focus:outline-none focus:border-[#46A8F9] transition-colors"
  const inputStyle = { color: '#15354F' }

  return (
    <>
      <ProfileSubpageLayout sectionId="insurance" icon={Shield} onSave={handleSave} saving={isProcessing || cardUploads > 0} busyLabel={cardUploads > 0 ? t('customDoc.uploading') : undefined} canSave={canSave}>
        {/* ★ U2-INS C5 (TEST 3.09): what the autosave is doing. */}
        {saveState !== 'idle' && (
          <p data-autosave={saveState} role="status" className={`text-[11px] font-medium mb-3 ${saveState === 'failed' ? 'text-red-500' : 'text-gray-400'}`}>
            {saveState === 'saving' ? t('common.saving') : saveState === 'saved' ? t('common.saved') : t('insuranceSearch.saveFailed')}
          </p>
        )}
        {/* v222.0.0 Stedi — Eligibility status (renders only when a check has been run) */}
        {/* v222.4.11 — inline 'Re-scan card' / 'Edit member ID' CTAs surface
            when the result class is not_found or data_mismatch. */}
        {/* PROD-431 item 91: over the region card, no "Setting up" (payer_not_mapped) badge */}
        {/* Q-C5: a changed member ID at the limit: the badge draws only the notice, no verdict */}
        {(profile?.insurance?.last_eligibility || eligibilityChecking || verdictWithheld(limitNotice)) && !badgeHiddenByRegionCard(regionRecord, profile?.insurance?.last_eligibility) && (
          <div className="mb-4">
            <EligibilityBadge
              eligibility={profile?.insurance?.last_eligibility}
              variant="detailed"
              comprehensive={true}
              loading={eligibilityChecking}
              onRecheck={handleRecheckEligibility}
              limitNotice={noticeForCoverage(limitNotice, form) /* PROD-936: a reuse notice holds only while the form names its coverage */}
              onRescanCard={() => setScannerOpen('cardFront')}
              onEditMemberId={() => {
                const el = memberIdInputRef.current
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                  setTimeout(() => el.focus(), 350)
                }
              }}
            />
            {/* PROD-581 (V-1): the entry to "My insurance". Drawn only while the switch is on
                (OFF) and the UI language has approved text for it (not es until Dr. Rami). */}
            {MY_INSURANCE_SCREEN && hasText('myInsurance.seeBenefits') && !eligibilityChecking && (
              <button data-see-benefits onClick={() => navigate(MY_INSURANCE_PATH)} className="mt-2 w-full flex items-center justify-between text-[12.5px] font-bold px-1" style={{ color: '#46A8F9' }}>
                <span>{t('myInsurance.seeBenefits')}</span>
                <ChevronRight size={15} />
              </button>
            )}
          </div>
        )}

        {/* Card Scanner */}
        <div className="rounded-[14px] p-4 mb-5 border-[1.5px]" style={{ borderColor: 'rgba(70,168,249,0.2)', background: 'rgba(233,248,255,0.4)' }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(70,168,249,0.1)' }}>
              <Scan size={20} style={{ color: '#46A8F9' }} />
            </div>
            <div>
              <h3 className="text-[13px] font-bold" style={{ color: '#15354F' }}>{t('profileInsurance2.scanYourCard')}</h3>
              <p className="text-[11px] text-gray-400">{t('profileInsurance2.autofillsPlanTypeCarrierIpa')}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {['cardFront', 'cardBack'].map((side) => (
              <div key={side}>
                {form[side] ? (
                  <div className="relative aspect-[1.586/1] bg-white rounded-xl overflow-hidden border border-gray-200">
                    <CardThumb card={form[side]} resolved={cardUrls[side]} alt={side} />
                    <div className="absolute top-1 left-1 bg-green-500 text-white text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                      <Check size={10} />{side === 'cardFront' ? t('profileInsurance.front') : t('profileInsurance.back')}
                    </div>
                    <button onClick={() => removeFile(side)} className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow"><X size={12} /></button>
                    <button onClick={() => setScannerOpen(side)} aria-label="Retake photo" className="absolute bottom-1 right-1 w-6 h-6 rounded-full flex items-center justify-center shadow" style={{ background: '#15354F', color: '#fff' }}><Scan size={12} /></button>
                  </div>
                ) : (
                  <button onClick={() => setScannerOpen(side)} className="w-full aspect-[1.586/1] border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-1.5 active:scale-[0.97] transition-all" style={{ background: 'rgba(255,255,255,0.7)' }}>
                    <Scan size={22} className="text-gray-400" />
                    <span className="text-[11px] font-medium text-gray-400">{side === 'cardFront' ? t('profileInsurance.scanFront') : t('profileInsurance.scanBack')}</span>
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-gray-100 my-5" />

        {/* Coverage Type */}
        <p className="text-[11px] font-bold uppercase tracking-widest mb-3 pb-1 inline-block" style={{ color: '#15354F', borderBottom: '1.5px solid #15354F' }}>{t('profileInsurance2.coverageType')}</p>
        <div className="grid grid-cols-2 gap-2 mb-5">
          {COVERAGE_TYPES.map(ct => {
            const isSelected = form.coverageType === ct.id
            return (
              <button key={ct.id} onClick={() => selectCoverage(ct.id)}
                className={`relative px-3 py-2.5 rounded-[14px] border-[1.5px] text-left transition-all active:scale-[0.97] ${isSelected ? 'border-[#46A8F9] bg-[#E9F8FF]' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <span className={`text-[13px] font-semibold ${isSelected ? '' : 'text-gray-600'}`} style={isSelected ? { color: '#15354F' } : {}}>{t('profileInsurance2.' + ct.labelKey) || ct.label}</span>
                  {isSelected && <div className="w-4 h-4 rounded-full bg-[#46A8F9] flex items-center justify-center flex-shrink-0"><Check size={10} className="text-white" /></div>}
                </div>
                <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">{t('profileInsurance2.' + ct.descKey)}</p>
              </button>
            )
          })}
        </div>

        {/* Carrier Selection */}
        {needsCarrier && (
          <>
            <div className="border-t border-gray-100 my-5" />
            <p className="text-[11px] font-bold uppercase tracking-widest mb-3 pb-1 inline-block" style={{ color: '#15354F', borderBottom: '1.5px solid #15354F' }}>{t('profileInsurance2.insuranceCarrier')}</p>
            {/* ★ L1 step 6 / D4 (PROD-188): the server stored no payer, only the regional
                siblings of the carrier name (Kaiser: Northern / Southern California ...).
                There is nothing to confirm, so the patient picks the region; the pick is
                the whole payer key set and Save stores it (utils/payerPick.js regionChoices). */}
            {regionChoices(regionRecord) ? (
              <div className="mb-3 rounded-[14px] p-3" data-region-pick="profile" style={{ background: '#E9F8FF', border: '1.5px solid #A7D9FC' }}>
                <p className="text-[12px] font-semibold mb-2" style={{ color: '#5b6b7a' }}>
                  {t('profileInsurance2.pickRegionPrompt', 'Your insurance has plans for more than one region. Which one is on your card?')}
                </p>
                <div className="flex flex-col gap-1.5">
                  {regionChoices(regionRecord).map(c => (
                    <button key={c.payerId} onClick={() => setForm(prev => ({ ...prev, ...regionPick(c) }))}
                      className="w-full text-left px-3 py-2 rounded-[10px] border border-gray-200 bg-white text-[13px] font-semibold"
                      style={{ color: '#15354F' }}>
                      {c.displayName}
                    </button>
                  ))}
                </div>
                <button onClick={() => { setShowCarrierList(true); setForm(prev => ({ ...prev, ...payerPick({ kind: 'clear' }) })) }}
                  className="mt-2.5 w-full h-9 rounded-full text-[13px] font-semibold" style={{ background: '#fff', color: '#15354F', border: '1.5px solid #D8E6F0' }}>
                  {t('profileInsurance2.confirmPlanChange', 'Pick a different one')}
                </button>
              </div>
            ) : form.payerNeedsConfirmation && !regionSettled(regionRecord) && (form.stediPayerName || form.provider) && (
              <div className="mb-3 rounded-[14px] p-3" style={{ background: '#E9F8FF', border: '1.5px solid #A7D9FC' }}>
                <p className="text-[12px] font-semibold mb-1.5" style={{ color: '#5b6b7a' }}>
                  {t('profileInsurance2.confirmPlanPrompt', 'We matched your card to this plan — is it right?')}
                </p>
                <div className="flex items-center gap-2 mb-2.5">
                  <Shield size={15} style={{ color: '#46A8F9' }} />
                  <span className="text-[14px] font-bold" style={{ color: '#15354F' }}>{form.stediPayerName || form.provider}</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setForm(prev => ({ ...prev, payerNeedsConfirmation: false }))}
                    className="flex-1 h-9 rounded-full text-[13px] font-semibold text-white" style={{ background: '#46A8F9' }}>
                    {t('profileInsurance2.confirmPlanYes', "Yes, that's my plan")}
                  </button>
                  <button onClick={() => { setShowCarrierList(true); setForm(prev => ({ ...prev, ...payerPick({ kind: 'clear' }) })) }}
                    className="flex-1 h-9 rounded-full text-[13px] font-semibold" style={{ background: '#fff', color: '#15354F', border: '1.5px solid #D8E6F0' }}>
                    {t('profileInsurance2.confirmPlanChange', 'Pick a different one')}
                  </button>
                </div>
                {/* v242.64.0 (TF-11): the server ranks up to 5 alternates and sends
                    them as payerCandidates. Nothing had ever rendered them, so the
                    only way out of a wrong match was a live search that cannot
                    answer while the payer directory is unreachable. */}
                {Array.isArray(form.payerCandidates) && form.payerCandidates.length > 1 && (
                  <div className="mt-3">
                    <p className="text-[11px] font-semibold mb-1.5" style={{ color: '#5b6b7a' }}>
                      {t('profileInsurance2.otherMatches', 'Other close matches')}
                    </p>
                    <div className="flex flex-col gap-1.5">
                      {form.payerCandidates
                        .filter(c => c && c.displayName && c.displayName !== (form.stediPayerName || form.provider))
                        .slice(0, 4)
                        .map(c => (
                          <button key={c.payerId || c.displayName}
                            onClick={() => setForm(prev => ({ ...prev,
                              ...payerPick({ kind: 'alternate', payerId: c.payerId, name: c.displayName, eligibility: c.eligibility }) }))}
                            className="w-full text-left px-3 py-2 rounded-[10px] border border-gray-200 bg-white text-[13px]"
                            style={{ color: '#15354F' }}>
                            {c.displayName}
                          </button>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {form.provider && !showCarrierList ? (
              <div className="mb-3">
                <div className="rounded-[14px] border-[1.5px] border-[#46A8F9] bg-[#E9F8FF] px-3.5 py-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#46A8F9] flex items-center justify-center flex-shrink-0"><Check size={12} className="text-white" /></div>
                    <span className="text-[13px] font-semibold" style={{ color: '#15354F' }}>{form.provider}</span>
                  </div>
                  <button onClick={() => { setShowCarrierList(true); setForm(prev => ({ ...prev, ...payerPick({ kind: 'clear' }) })) }}
                    className="text-[11px] font-semibold" style={{ color: '#46A8F9' }}>{t('profileInsurance2.change')}</button>
                </div>
              </div>
            ) : (
              <div className="mb-4">
                <div className="relative mb-2">
                  <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="text" value={carrierQuery} onChange={(e) => setCarrierQuery(e.target.value)}
                    placeholder={t('profileInsurance2.searchCarriers')} className="w-full pl-10 pr-10 py-2.5 border-[1.5px] border-gray-200 rounded-[14px] text-[13px] focus:outline-none focus:border-[#46A8F9] transition-colors" style={{ color: '#15354F' }} />
                  {carrierQuery && <button onClick={() => setCarrierQuery('')} className="absolute right-3.5 top-1/2 -translate-y-1/2"><X size={16} className="text-gray-400" /></button>}
                </div>
                <div className="space-y-1.5 mb-2">
                  {!carrierQ && (
                    <>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-gray-300 px-1 mb-1">{t('profileInsurance2.commonCarriers')}</p>
                      <div className="flex flex-wrap gap-1.5 px-1">
                        {['Aetna','Cigna','UnitedHealthcare','Blue Shield of California','Anthem Blue Cross'].map(n => (
                          <button key={n} onClick={() => setCarrierQuery(n)}
                            className="px-3 py-1.5 rounded-full border-[1.5px] border-gray-200 text-[12px] font-medium text-gray-600 active:border-[#46A8F9] active:bg-[#E9F8FF]">{n}</button>
                        ))}
                      </div>
                    </>
                  )}
                  {carrierQ && stediLoading && (
                    <div className="flex items-center justify-center py-4 gap-2 text-gray-400"><OrellaSpinner size={16} tone="inherit" /><span className="text-[12px]">{t('profileInsurance2.searchingPlans', 'Searching plans')}</span></div>
                  )}
                  {carrierQ && !stediLoading && stediResults.map(p => (
                    <button key={p.stediId || p.primaryPayerId} onClick={() => selectCarrier(p)}
                      className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-[14px] border-[1.5px] border-gray-200 transition-colors active:border-[#46A8F9] active:bg-[#E9F8FF] text-left">
                      <div className="w-5 h-5 rounded-full border-[1.5px] border-gray-300 flex items-center justify-center flex-shrink-0" />
                      <span className="flex-1 min-w-0">
                        <span className="text-[13px] font-medium text-gray-700 block truncate">{p.displayName}</span>
                        {p.operatingStates && p.operatingStates.length > 0 && (
                          <span className="text-[10px] text-gray-400 block truncate">{p.operatingStates.slice(0, 4).join(', ')}</span>
                        )}
                      </span>
                      {p.eligibility === 'SUPPORTED' && <Check size={14} className="text-[#66D170] flex-shrink-0" />}
                    </button>
                  ))}
                  {carrierQ && !stediLoading && stediResults.length === 0 && (
                    <p className="text-[12px] text-gray-400 text-center py-3 leading-relaxed">
                      {stediError
                        ? t('profileInsurance2.searchUnavailable', "Can't reach the plan directory right now — you can still use the name you typed below.")
                        : t('profileInsurance2.noMatchingPlan', 'No matching plan found')}
                    </p>
                  )}
                </div>
                {carrierQ && !stediLoading && (
                  <button onClick={() => selectCarrier({ id: 'custom', name: carrierQuery.trim() })}
                    className="w-full py-2.5 border-[1.5px] border-dashed rounded-[14px] text-[13px] font-semibold flex items-center justify-center gap-2"
                    style={{ borderColor: 'rgba(70,168,249,0.3)', color: '#46A8F9' }}>+ Use &ldquo;{carrierQuery.trim()}&rdquo;</button>
                )}
              </div>
            )}
          </>
        )}

        {/* IPA Selection (HMO only) */}
        {needsIPA && (
          <>
            <div className="border-t border-gray-100 my-5" />
            <p className="text-[11px] font-bold uppercase tracking-widest mb-1 pb-1 inline-block" style={{ color: '#15354F', borderBottom: '1.5px solid #15354F' }}>{t('profileInsurance2.medicalGroupIpa')}</p>
            <p className="text-[11px] text-gray-400 mb-3">{t('profileInsurance2.foundOnYourHmoCard')}</p>
            {form.ipa ? (
              <div className="mb-3">
                <div className="rounded-[14px] border-[1.5px] px-3.5 py-2.5 flex items-center justify-between" style={{ borderColor: 'rgba(102,209,112,0.5)', background: 'rgba(102,209,112,0.06)' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#66D170' }}><Check size={12} className="text-white" /></div>
                    <span className="text-[13px] font-semibold" style={{ color: '#15354F' }}>{form.ipa}</span>
                  </div>
                  <button onClick={() => setForm(prev => ({ ...prev, ipa: '', ipaId: '' }))} className="text-[11px] font-semibold" style={{ color: '#46A8F9' }}>{t('profileInsurance2.change')}</button>
                </div>
              </div>
            ) : (
              <div className="mb-4">
                <div className="relative mb-2">
                  <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="text" value={ipaQuery} onChange={(e) => setIpaQuery(e.target.value)}
                    placeholder={t('profileInsurance2.searchIPAs')} className="w-full pl-10 pr-10 py-2.5 border-[1.5px] border-gray-200 rounded-[14px] text-[13px] focus:outline-none focus:border-[#46A8F9] transition-colors" style={{ color: '#15354F' }} />
                  {ipaQuery && <button onClick={() => setIpaQuery('')} className="absolute right-3.5 top-1/2 -translate-y-1/2"><X size={16} className="text-gray-400" /></button>}
                </div>
                {ipaQ && (
                  <div className="space-y-1.5 mb-2">
                    {filteredIPAs.length > 0 ? filteredIPAs.slice(0, 8).map(item => (
                      <button key={item.id} onClick={() => selectIPA(item)}
                        className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-[14px] border-[1.5px] border-gray-200 transition-colors active:border-[#66D170] active:bg-green-50">
                        <div className="w-5 h-5 rounded-full border-[1.5px] border-gray-300 flex items-center justify-center flex-shrink-0" />
                        <span className="text-[13px] font-medium text-gray-600 block truncate flex-1 text-left">{item.name}</span>
                        {item.prior_auth_required && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 font-medium flex-shrink-0">{t('profileInsurance2.priorAuth')}</span>}
                      </button>
                    )) : (
                      <div className="text-center py-3">
                        <p className="text-[12px] text-gray-400 mb-2">{t('profileInsurance2.noMatches')}</p>
                        <button onClick={() => selectIPA(ipaQuery.trim())}
                          className="py-2 px-4 border-[1.5px] border-dashed rounded-[14px] text-[12px] font-semibold"
                          style={{ borderColor: 'rgba(70,168,249,0.3)', color: '#46A8F9' }}>+ Use &ldquo;{ipaQuery.trim()}&rdquo;</button>
                      </div>
                    )}
                  </div>
                )}
                <button onClick={() => setForm(prev => ({ ...prev, ipa: '', ipaId: 'skipped' }))}
                  className="w-full py-2.5 border-[1.5px] border-dashed rounded-[14px] text-[12px] font-semibold flex items-center justify-center gap-2"
                  style={{ borderColor: 'rgba(70,168,249,0.3)', color: '#46A8F9' }}>{t('profileInsurance2.iDonapostKnowMyIpa')}</button>
              </div>
            )}
          </>
        )}

        {/* Member Details */}
        {needsMember && (
          <>
            <div className="border-t border-gray-100 my-5" />
            <p className="text-[11px] font-bold uppercase tracking-widest mb-3 pb-1 inline-block" style={{ color: '#15354F', borderBottom: '1.5px solid #15354F' }}>{t('profileInsurance2.memberDetails')}</p>
            {form.coverageType === 'medicare' && userId && (
              <MedicareIdLookup
                userId={userId}
                onResolved={(mbi) => setForm(prev => ({ ...prev, memberId: mbi }))}
              />
            )}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-[12px] text-gray-500 mb-1">{t('profileInsurance.memberId')} <span className="text-red-500">*</span></label>
                <input type="text" ref={memberIdInputRef} value={form.memberId} onChange={(e) => setForm({...form, memberId: e.target.value})} className={inputCls} style={inputStyle} placeholder={t('profileInsurance2.memberIdPlaceholder')} />
              </div>
              <div>
                <label className="block text-[12px] text-gray-500 mb-1">{t('profileInsurance.groupNumber')}</label>
                <input type="text" value={form.groupNumber} onChange={(e) => setForm({...form, groupNumber: e.target.value})} className={inputCls} style={inputStyle} placeholder={t('profileInsurance2.groupPlaceholder')} />
              </div>
            </div>
            <div className="border-t border-gray-100 my-5" />
            <p className="text-[11px] font-bold uppercase tracking-widest mb-3 pb-1 inline-block" style={{ color: '#15354F', borderBottom: '1.5px solid #15354F' }}>{t('profileInsurance2.subscriber')}</p>
            <div className="mb-4">
              <label className="block text-[12px] text-gray-500 mb-1">{t('profileInsurance2.subscriberRelationship')}</label>
              <div className="grid grid-cols-3 gap-2">
                {[{ value: 'Self', label: t('profileInsurance2.me', 'Me') }, { value: 'Spouse', label: t('profileInsurance2.spouse') }, { value: 'Parent', label: t('profileInsurance2.parent') }, { value: 'Child', label: t('profileInsurance2.child', 'Child') }, { value: 'Other', label: t('profileInsurance2.other') }].map(opt => {
                  const isSel = form.subscriberRelationship === opt.value
                  return (
                    <button key={opt.value} onClick={() => setForm(prev => ({ ...prev, subscriberRelationship: opt.value }))}
                      className={`py-2.5 rounded-[14px] text-[13px] font-semibold transition-all border-[1.5px] ${isSel ? 'border-[#46A8F9] bg-[#E9F8FF]' : 'border-gray-200 text-gray-400'}`}
                      style={isSel ? { color: '#15354F' } : {}}>{opt.label}</button>
                  )
                })}
              </div>
            </div>
            {showSubscriberInfo && (
              <div className="rounded-[14px] border-[1.5px] border-gray-200 p-4 mb-4">
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3">{t('profileInsurance2.subscriberInformation')}</p>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-[12px] text-gray-500 mb-1">{t('profileId.firstName')}</label>
                    <input type="text" value={form.subscriberFirstName} onChange={(e) => setForm({...form, subscriberFirstName: e.target.value})} className={inputCls} style={inputStyle} />
                  </div>
                  <div>
                    <label className="block text-[12px] text-gray-500 mb-1">{t('profileId.lastName')}</label>
                    <input type="text" value={form.subscriberLastName} onChange={(e) => setForm({...form, subscriberLastName: e.target.value})} className={inputCls} style={inputStyle} />
                  </div>
                </div>
                <div>
                  {/* TF-01 (v242.61.0) — "This is still the old DOB field which I have
                      to enter for the insurance". It was: a raw <input type="date">,
                      while ProfileIdentification has used the shared <DobPicker> since
                      it was introduced. Same control everywhere now. */}
                  <label className="block text-[12px] text-gray-500 mb-1">{t('profileId.dob')}</label>
                  <DobPicker value={form.subscriberDOB} onChange={(v) => setForm({...form, subscriberDOB: v})} />
                </div>
              </div>
            )}

            {/* ★ U2-INS C2 (TEST 3.02): search for the primary with the details above. */}
            {INSURANCE_DISCOVERY_ENABLED && (
            <div className="mb-4" data-insurance-search="primary">
              <button type="button" onClick={handleSearch} disabled={searchBlocked}
                className="w-full py-2.5 border-[1.5px] rounded-[14px] text-[13px] font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ borderColor: 'rgba(70,168,249,0.45)', color: '#46A8F9', background: '#fff' }}>
                {searchStatus === 'searching' ? <OrellaSpinner size={16} tone="inherit" /> : <Search size={15} />}
                {t('insuranceSearch.searchButton')}
              </button>
              {searchesLeft !== null && (
                <p className="text-[11px] text-gray-400 text-center mt-1.5">{t('insuranceSearch.searchesLeft', { count: Math.max(0, Math.min(DISCOVERY_CAP, searchesLeft)) })}</p>
              )}
              {searchStatus === 'searching' && (
                <p className="text-[12px] text-gray-400 text-center mt-2">{t('profileInsurance2.searchingPlans')}</p>
              )}
              {outcomeMessageKey(searchStatus, 'profile') && (
                <p role="status" data-search-outcome={searchStatus} className="text-[12px] mt-2 leading-relaxed" style={{ color: '#15354F' }}>{t(outcomeMessageKey(searchStatus, 'profile'))}</p>
              )}
              {searchStatus === 'found' && searchCandidates.length > 0 && (
                <div className="mt-3">
                  <InsuranceCandidateList
                    candidates={searchCandidates}
                    variant="inline"
                    singleConfirm={searchCandidates.length === 1}
                    onApplyPrimary={applySearchCandidate}
                    onDone={() => { setSearchStatus('idle'); setSearchCandidates([]) }}
                    onSkip={() => { setSearchStatus('idle'); setSearchCandidates([]) }}
                  />
                </div>
              )}
            </div>
            )}

            {/* Copayment */}
            <div className="border-t border-gray-100 my-5" />
            <p className="text-[11px] font-bold uppercase tracking-widest mb-3 pb-1 inline-block" style={{ color: '#15354F', borderBottom: '1.5px solid #15354F' }}>{t('profileInsurance2.copayment')}</p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-[12px] text-gray-500 mb-1">{t('profileInsurance2.copayPCP')}</label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[13px] text-gray-400 font-medium">$</span>
                  <input type="number" inputMode="decimal" value={form.copayPCP} onChange={(e) => setForm({...form, copayPCP: e.target.value})} className={inputCls} style={{ ...inputStyle, paddingLeft: 24 }} placeholder="0" />
                </div>
              </div>
              <div>
                <label className="block text-[12px] text-gray-500 mb-1">{t('profileInsurance2.copaySpecialist')}</label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[13px] text-gray-400 font-medium">$</span>
                  <input type="number" inputMode="decimal" value={form.copaySpecialist} onChange={(e) => setForm({...form, copaySpecialist: e.target.value})} className={inputCls} style={{ ...inputStyle, paddingLeft: 24 }} placeholder="0" />
                </div>
              </div>
            </div>
            <PrimaryCareDoctor value={form.pcp} cardPcp={form.pcpFromCard} coverageType={form.coverageType}
              zip={profile?.zipCode || profile?.address?.zipCode || profile?.address?.zip || ''}
              onChange={(pcp) => setForm((prev) => ({ ...prev, pcp }))} t={t} />
            {userId && (
              <SecondaryInsuranceCard
                userId={userId}
                secondary={profile?.insurance?.secondary || null}
                secondaryEligibility={profile?.insurance?.last_eligibility_secondary || null}
                secondaryDiscovery={profile?.insurance?.secondary_discovery_result || null}
                runExclusive={runExclusive}
                onSaved={(result) => {
                  const currentIns = useProfileStore.getState().profile?.insurance || {}
                  // PROD-689 client half (P4-4): the secondary check's answer through the one
                  //   reader; a limit answer's null keeps the stored secondary result.
                  const answer = eligibilityAnswer(result)
                  updateProfile({ insurance: {
                    ...currentIns,
                    secondary: result?.secondary || null,
                    // PROD-1133: the stored result is a fallback only for the SAME coverage; a changed plan shows "not checked".
                    last_eligibility_secondary: answer.eligibility || (result?.secondary && sameSecondaryCoverage(currentIns.secondary, result.secondary) ? currentIns.last_eligibility_secondary : null),
                  } })
                }}
                onPolicyholderSaved={(sec) => {
                  const currentIns = useProfileStore.getState().profile?.insurance || {}
                  updateProfile({ insurance: { ...currentIns, secondary: sec } })
                }}
                onSecondaryDiscovery={(dr) => {
                  const currentIns = useProfileStore.getState().profile?.insurance || {}
                  updateProfile({ insurance: { ...currentIns, secondary_discovery_result: dr } })
                }}
              />
            )}
          </>
        )}
      </ProfileSubpageLayout>

      {scannerOpen && (
        <CardScanner cardType="Insurance" side={scannerOpen === 'cardFront' ? 'front' : 'back'}
          onCapture={(cardData) => handleCardCapture(scannerOpen, cardData)} onCancel={() => setScannerOpen(null)}
          onUpload={async (file) => { const side = scannerOpen; const scanToken = cardPair.beginSide(side); try { const cardData = await processCardFile(file, 'Insurance', side === 'cardFront' ? 'front' : 'back', { patientState: profile?.identification?.state || '' }); handleCardCapture(side, cardData, scanToken) } catch (e) { if (!cardPair.isCurrent(scanToken)) return; alert(e?.message || 'Could not read that image — try another photo.') } }}
          initialImage={cardUrls[scannerOpen] || getImageUrl(form[scannerOpen])} enableOCR={scannerOpen === 'cardFront'}
          patientState={profile?.identification?.state || ''} />
      )}
    </>
  )
}
